import * as React from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Check } from 'lucide-react'
import { cn } from '@/lib/cn'

export interface DropdownItem {
  key: string
  label: string
  icon?: React.ReactNode
  onSelect?: () => void
  danger?: boolean
  /** Present (true *or* false) turns the row into a checkable item. */
  checked?: boolean
  separatorBefore?: boolean
  disabled?: boolean
  /**
   * Let this row grow and wrap instead of truncating to one line.
   *
   * For rows that carry a SENTENCE rather than a command — the reason a menu has
   * nothing to offer, or a caption under the actions. Those are exactly the rows
   * where the tail matters, and `truncate` throws the tail away: "This console is
   * set to US, and the login link is built for…" is worse than useless cut off
   * at the width of a menu.
   */
  wrap?: boolean
}

const ITEM_BASE =
  'relative flex h-8 cursor-default items-center gap-2 rounded-sm px-2 text-[13.5px] ' +
  'text-fg outline-none select-none transition-colors duration-75 ' +
  '[&_svg]:size-[15px] [&_svg]:shrink-0 ' +
  'data-highlighted:bg-s3 data-highlighted:text-fg ' +
  'data-disabled:pointer-events-none data-disabled:opacity-45 ' +
  // A menu is a list of thumb targets on a phone; 32px rows are not.
  '[@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:text-[15px]'

const ITEM_DANGER = 'text-danger data-highlighted:bg-danger-soft data-highlighted:text-danger'

// `items-start` so an icon sits with the FIRST line of a wrapped sentence rather
// than floating in the middle of the block.
const ITEM_WRAP = 'h-auto min-h-8 items-start py-1.5 [@media(pointer:coarse)]:h-auto'

export function Dropdown(p: {
  trigger: React.ReactNode
  items: DropdownItem[]
  align?: 'start' | 'end'
  label?: string
  /**
   * Fires on open and on close.
   *
   * Added for menus whose contents have to be FETCHED. Doing that inside an
   * item's `onSelect` does not work: `window.open` and clipboard writes are only
   * permitted inside a user gesture, and awaiting a request first spends the
   * activation — so the popup is blocked and the copy silently fails. Opening
   * the menu is itself a gesture and happens a moment earlier, which makes it
   * the right place to start the request and leaves each item's own click
   * synchronous.
   */
  onOpenChange?: (open: boolean) => void
  /**
   * Pass `false` when this menu is inside a Drawer or Dialog.
   *
   * A modal menu inside a modal dialog stacks two focus traps and two
   * `pointer-events: none` locks on `body`, and the inner one can outlive its
   * own close — leaving the dialog behind it unclickable. Nothing is lost by
   * switching it off in that position: the surrounding dialog is already
   * trapping focus, and outside-click and Escape still dismiss the menu.
   */
  modal?: boolean
}) {
  // If any row is checkable, every row reserves the indicator gutter so the
  // labels stay on one vertical line.
  const hasChecks = p.items.some((i) => i.checked !== undefined)

  return (
    <DropdownMenu.Root onOpenChange={p.onOpenChange} modal={p.modal}>
      <DropdownMenu.Trigger asChild>{p.trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={p.align ?? 'start'}
          sideOffset={6}
          collisionPadding={8}
          aria-label={p.label}
          className={cn(
            'z-[60] max-h-[min(70dvh,26rem)] min-w-[200px] overflow-y-auto overscroll-contain',
            'rounded-lg border border-line bg-s2 p-1 shadow-e2',
            'origin-(--radix-dropdown-menu-content-transform-origin)',
            'transition-[opacity,transform] duration-100 ease-out',
            'starting:scale-[0.97] starting:opacity-0'
          )}
        >
          {p.items.map((item) => {
            const gutter = hasChecks ? (
              <span className="flex size-4 flex-none items-center justify-center text-accent">
                {item.checked !== undefined ? (
                  <DropdownMenu.ItemIndicator>
                    <Check className="size-4" aria-hidden="true" />
                  </DropdownMenu.ItemIndicator>
                ) : null}
              </span>
            ) : null

            const body = (
              <>
                {gutter}
                {item.icon ? (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex flex-none items-center text-faint',
                      // On a wrapped row the flex line is the whole block, so
                      // nudge the icon onto the first line's optical centre.
                      item.wrap && 'mt-0.5'
                    )}
                  >
                    {item.icon}
                  </span>
                ) : null}
                <span
                  className={cn(
                    'flex-1',
                    item.wrap
                      ? // The cap is what makes it wrap at all: the menu has a
                        // min width and no max, so an unbounded sentence would
                        // just widen the panel off the side of a phone instead
                        // of breaking onto a second line. 17rem leaves the whole
                        // row inside a 360px viewport.
                        'max-w-[17rem] whitespace-normal break-words leading-[1.45]'
                      : 'truncate'
                  )}
                >
                  {item.label}
                </span>
              </>
            )

            return (
              <React.Fragment key={item.key}>
                {item.separatorBefore ? (
                  <DropdownMenu.Separator className="my-1 h-px bg-line" />
                ) : null}

                {item.checked !== undefined ? (
                  <DropdownMenu.CheckboxItem
                    checked={item.checked}
                    disabled={item.disabled}
                    onSelect={() => item.onSelect?.()}
                    className={cn(ITEM_BASE, item.wrap && ITEM_WRAP, item.danger && ITEM_DANGER)}
                  >
                    {body}
                  </DropdownMenu.CheckboxItem>
                ) : (
                  <DropdownMenu.Item
                    disabled={item.disabled}
                    onSelect={() => item.onSelect?.()}
                    className={cn(ITEM_BASE, item.wrap && ITEM_WRAP, item.danger && ITEM_DANGER)}
                  >
                    {body}
                  </DropdownMenu.Item>
                )}
              </React.Fragment>
            )
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
